import { useParams, Link } from 'react-router-dom';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function MovieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(Number(id));

  if (!movie) {
    return (
      <>
        <title>影片未找到 | 星空影院在线观看免费</title>
        <div className="pt-24 pb-20 px-4 md:px-6">
          <div className="max-w-[1280px] mx-auto text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-800/60">
              <i className="ri-error-warning-line text-foreground-500 text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-50 mb-3">影片未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要找的影片不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 bg-primary-500 text-background-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
      </>
    );
  }

  const relatedMovies = getRelatedMovies(movie, 6);
  const pageTitle = `${movie.title} | 星空影院在线观看免费`;
  const pageDesc = `${movie.title}，${movie.year}年上映，导演${movie.director}，主演${movie.cast.slice(0, 3).join('、')}等。${movie.description.substring(0, 80)}`;
  const pageKeywords = `${movie.title},星空影院${movie.title},免费在线观看${movie.title},高清${movie.title},星空影院在线观看免费`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: `${movie.year}-01-01`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      ratingCount: Math.floor(Math.random() * 50000) + 10000,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://yukafile.github.io/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '电影分类',
        item: 'https://yukafile.github.io/category/all',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: movie.title,
        item: `https://yukafile.github.io/movie/${movie.id}`,
      },
    ],
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={`https://yukafile.github.io/movie/${movie.id}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={movie.poster} />
      <meta property="og:type" content="video.movie" />
      <meta property="og:url" content={`https://yukafile.github.io/movie/${movie.id}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={movie.poster} />
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      <div className="pt-20 md:pt-24 pb-10 md:pb-14 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '电影分类', to: '/category/all' },
              { label: movie.title },
            ]}
          />

          <article className="mt-4 md:mt-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-64 aspect-[2/3] rounded-lg overflow-hidden">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 海报`}
                    title={`${movie.title} - 星空影院在线观看免费`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
                  {movie.title}
                </h1>

                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary-500/20 text-primary-500 whitespace-nowrap">
                    评分 {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-400">{movie.year}</span>
                  <span className="text-sm text-foreground-400">{movie.duration}</span>
                  {movie.type === 'series' && (
                    <span className="px-2 py-1 rounded-full text-xs bg-secondary-500/20 text-secondary-500 whitespace-nowrap">
                      剧集
                    </span>
                  )}
                  {movie.isHot && (
                    <span className="px-2 py-1 rounded-full text-xs bg-accent-500/20 text-accent-500 whitespace-nowrap">
                      热门推荐
                    </span>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 whitespace-nowrap w-12 flex-shrink-0">导演</span>
                    <span className="text-sm text-foreground-300">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 whitespace-nowrap w-12 flex-shrink-0">主演</span>
                    <span className="text-sm text-foreground-300">{movie.cast.join(' / ')}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 whitespace-nowrap w-12 flex-shrink-0">类型</span>
                    <Link
                      to={`/category/${movie.genre}`}
                      className="text-sm text-primary-500 hover:text-primary-400 transition-colors duration-200 cursor-pointer"
                    >
                      {movie.genre === 'scifi' ? '科幻' :
                       movie.genre === 'action' ? '动作' :
                       movie.genre === 'comedy' ? '喜剧' :
                       movie.genre === 'drama' ? '剧情' :
                       movie.genre === 'horror' ? '恐怖' :
                       movie.genre === 'romance' ? '爱情' :
                       movie.genre === 'animation' ? '动画' :
                       movie.genre === 'documentary' ? '纪录片' : movie.genre}
                    </Link>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer hover:shadow-lg hover:shadow-primary-500/20">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-play-fill text-base"></i>
                    </div>
                    <span>立即播放</span>
                  </button>
                  <button className="px-6 py-3 bg-background-800/60 hover:bg-background-700/60 text-foreground-300 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer border border-background-700/50">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-heart-line text-base"></i>
                    </div>
                    <span>收藏</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 md:mt-10">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-100 mb-4">
                <a href="#synopsis">剧情简介</a>
              </h2>
              <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-3xl">
                {movie.description}
              </p>
            </div>

            {relatedMovies.length > 0 && (
              <div className="mt-10 md:mt-14">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-100">
                    <a href="#related">相关推荐</a>
                  </h3>
                  <Link
                    to={`/category/${movie.genre}`}
                    className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <span>更多</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>
      </div>
    </>
  );
}